import { useCallback, useMemo, useState, type SubmitEvent } from "react";
import { catchError, of, tap } from "rxjs";

import { APILibraryVertical } from "@/core/data_sources/models/APILibraryVertical";
import { Button, TextInput } from "@/core/ui/atoms";
import { useStore } from "@/core/utils/hooks";
import { ErrorResponse } from "@/core/utils/request";

import { LibraryVerticalsStore } from "../data_sources/store";

type CreateVerticalDialogProps = {
  readonly onCancel: () => void;
  readonly onCreated: () => void;
};

export function CreateVerticalDialog({
  onCancel,
  onCreated,
}: CreateVerticalDialogProps) {
  const { actions, isConnected } = useStore(LibraryVerticalsStore.instance);
  const [vertical, setVertical] = useState(
    new APILibraryVertical({ name: "", code: "" }),
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const canSubmit = useMemo(
    () => isConnected && vertical.isDataValid() && !saving,
    [isConnected, saving, vertical],
  );

  const handleSubmit = useCallback(
    (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!canSubmit) {
        setError("Code and name are required.");
        return;
      }

      setSaving(true);
      setError("");
      actions
        .createVertical(vertical.toRequestModel())
        .pipe(
          tap(() => {
            onCreated();
          }),
          catchError((requestError) => {
            setError(
              requestError instanceof ErrorResponse
                ? requestError.error.message
                : "Unable to create vertical.",
            );
            return of(null);
          }),
          tap(() => setSaving(false)),
        )
        .subscribe();
    },
    [actions, canSubmit, onCreated, vertical],
  );

  return (
    <form
      className="flex max-h-[calc(100vh-7.5rem)] w-120 max-w-full flex-col overflow-hidden"
      onSubmit={handleSubmit}
    >
      <div className="shrink-0 border-b border-border px-6 pb-3 pt-5">
        <h2 className="text-[17px] font-semibold text-ink">Create vertical</h2>
        <p className="mt-1 text-[13px] text-ink-subtle">
          Verticals are global. Once created, all tenants can use them.{" "}
          <strong>Rename is not available in V1</strong> — choose the name
          carefully.
        </p>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
        <label className="block">
          <div className="mb-1 flex flex-row gap-1 items-center">
            <span className="block text-xs font-medium text-ink-muted">
              Code
            </span>
            <span className="text-xs font-medium text-red-500 pb-2">*</span>
            <span className="text-xs font-thin text-ink-muted">
              uppercase, A-Z and _ only · immutable
            </span>
          </div>
          <TextInput
            autoFocus
            onChange={(event) =>
              setVertical(
                (prev) =>
                  new APILibraryVertical({
                    ...prev.data,
                    code: event.target.value,
                  }),
              )
            }
            placeholder="HVAC"
            value={vertical.data.code}
          />
        </label>
        <label className="block">
          <div className="flex flex-row gap-1 items-center mb-1">
            <span className="block text-xs font-medium text-ink-muted">
              Display Name
            </span>
            <span className="text-xs font-medium text-red-500 pb-2">*</span>
          </div>
          <TextInput
            onChange={(event) =>
              setVertical(
                (prev) =>
                  new APILibraryVertical({
                    ...prev.data,
                    name: event.target.value,
                  }),
              )
            }
            placeholder="HVAC"
            value={vertical.data.name}
          />
        </label>
        {error ? <p className="text-xs text-critical">{error}</p> : null}
      </div>

      <div className="flex shrink-0 justify-end gap-2 border-t border-border bg-panel-muted px-6 py-3.5">
        <Button disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={!canSubmit} type="submit" variant="primary">
          {saving ? "Creating..." : "Create vertical"}
        </Button>
      </div>
    </form>
  );
}
